// `/app/reports` (`spec/24-croqui-alinhamento.md` §5, WEB-55). Sem step-up: são três GETs
// e um POST que a API não protege com TOTP.
//
// O período E o drawer "Exportar CSV" vivem na query string (`spec/17` §3): recarregar,
// compartilhar o link e o botão de voltar continuam valendo. O drawer não é um
// `RouteDrawer` porque não há rota `$id` aqui (ver "Questões abertas" da task).

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { IsoRange } from '../api/queries/reports.ts';
import { useMode } from '../app/mode.tsx';
import { DEFAULT_PRESET, rangeForPreset } from '../features/reports/queries.ts';
import { ReportsScreen } from '../features/reports/reports-screen.tsx';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `?from=banana` cai em `undefined` (e no preset default) em vez de derrubar a rota. */
const searchSchema = z.object({
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
  novo: z.boolean().optional().catch(undefined),
});

function Reports() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { mode } = useMode();

  const fallback = rangeForPreset(DEFAULT_PRESET);
  const range: IsoRange = {
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
  };

  /** `undefined` em vez de `false`: a URL limpa é a do drawer fechado. */
  const go = (next: { from: string; to: string; novo: boolean }): void => {
    void navigate({
      to: '/app/reports',
      search: {
        ...(next.from === fallback.from ? {} : { from: next.from }),
        ...(next.to === fallback.to ? {} : { to: next.to }),
        ...(next.novo ? { novo: true } : {}),
      },
      replace: true,
    });
  };

  return (
    <ReportsScreen
      mode={mode}
      range={range}
      newExportOpen={search.novo === true}
      onRangeChange={(next) => {
        go({ ...next, novo: search.novo === true });
      }}
      onNewExportOpenChange={(open) => {
        go({ ...range, novo: open });
      }}
    />
  );
}

export const Route = createFileRoute('/app/reports/')({
  validateSearch: searchSchema,
  component: Reports,
});
